import useTerms from "../../../hooks/useTerms";
import React, { useEffect, useState } from "react";
import { Box, Drawer, Typography } from "@mui/material";
import { AssignmentTurnedIn as ConsentIcon } from "@mui/icons-material";
import { DrawerHeader, DrawerFooter, FormTextField, FormDatePicker } from "../../atoms";
import DrawerField from "../../molecules/DrawerField";
import { tileInputProps } from "../../molecules/InfoTile";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import { useConfirmSessionConsentPaperMutation } from "../../../store/traditionalApi";
import { splitDateTime, joinDateTime } from "./practiceSessionFormat";

/**
 * "Onay bekliyor" seansı kağıt (ıslak imza) onamla tamamlar. Telefondaki istek
 * iptal olur, seans tamamlanır (seçilen paket varsa düşülür). Şablon sunucuda
 * telefona giden istekten (yoksa kurala göre) alınır.
 *
 * @param {object} session - pending_consent seans
 * @param {(session) => void} onDone
 */
export default function SessionPaperConsentDrawer({ open, onClose, token, clinicId, session, patientName, onDone }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showError, showSuccess } = useAlert();
  const [confirmPaper, { isLoading: saving }] = useConfirmSessionConsentPaperMutation();

  const [signedBy, setSignedBy] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");

  useEffect(() => {
    if (!open) return;
    const now = splitDateTime(new Date());
    setSignedBy(patientName || "");
    setDate(now.date);
    setTime(now.time);
  }, [open, patientName]);

  const canSubmit = Boolean(session?._id && signedBy.trim() && date);

  const handleSave = async () => {
    if (!canSubmit) return;
    try {
      const res = await confirmPaper({
        token,
        ...(clinicId ? { clinicId } : {}),
        sessionId: session._id,
        signedBy: signedBy.trim(),
        signedAt: joinDateTime(date, time) || undefined,
      }).unwrap();
      showSuccess("Seans kağıt onamla tamamlandı");
      onDone?.(res?.data?.session);
      onClose?.();
    } catch (e) {
      showError(e?.data?.message || "Kağıt onam kaydedilemedi.");
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, display: "flex", flexDirection: "column", bgcolor: COLORS.white } }}
    >
      <DrawerHeader
        icon={<ConsentIcon />}
        title="Kağıtla Onayla"
        subtitle={session?.practiceName ? `${patientName || terms.patient} · ${session.practiceName}` : patientName}
        onClose={onClose}
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 2.5 }}>
        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600, mb: 2.5 }}>
          {terms.patient} onam formunu kağıt üzerinde imzaladıysa buradan kaydedin. Telefona giden onay isteği
          iptal olur ve seans tamamlanır.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", rowGap: 2.5, columnGap: 2 }}>
          <DrawerField label="İmzalayan *" fullWidth>
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              placeholder="Ad Soyad"
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              InputProps={tileInputProps(true)}
              inputProps={{ maxLength: 200, "aria-label": "İmzalayan" }}
            />
          </DrawerField>
          <DrawerField label="İmza tarihi">
            <FormDatePicker
              value={date}
              onChange={setDate}
              variant="standard"
              fullWidth
              clearable={false}
              maxDate={splitDateTime(new Date()).date}
            />
          </DrawerField>
          <DrawerField label="Saat">
            <FormTextField
              palette={palette}
              variant="standard"
              size="small"
              fullWidth
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              InputProps={tileInputProps(true)}
              inputProps={{ "aria-label": "İmza saati" }}
            />
          </DrawerField>
        </Box>
      </Box>

      <DrawerFooter
        onCancel={onClose}
        cancelLabel="Vazgeç"
        onSubmit={handleSave}
        submitLabel="Onayla ve Tamamla"
        loadingLabel="Kaydediliyor..."
        loading={saving}
        disabled={!canSubmit}
        fullWidth
      />
    </Drawer>
  );
}
